/**
 * API Service for Short Links Feature
 * Provides typed access to chart sharing endpoints
 */

import { devError } from '../utils/devLog';
import { AxiosError } from 'axios';
import api from './api';
import {
  CreateShortLinkRequest,
  CreateShortLinkResponse,
  SharedChartData,
  ShareAnalytics,
  MySharesResponse
} from '../types/share.types';

/**
 * API client for chart sharing functionality
 */
export class ShortLinkApiService {
  /**
   * Create a short link for chart sharing
   *
   * @param request - Jataka request, metadata, and share configuration
   * @returns Short code and full URL
   */
  async createShortLink(request: CreateShortLinkRequest): Promise<CreateShortLinkResponse> {
    try {
      const response = await api.post<CreateShortLinkResponse>(
        '/charts/share',
        request,
        { timeout: 10000 }
      );

      return response.data;
    } catch (error) {
      this.handleError(error, 'Failed to create short link');
      throw error;
    }
  }

  /**
   * Get shared chart data by short code
   *
   * @param shortCode - Short code from URL
   * @param asGuest - If true, skip JWT token (view as guest)
   * @returns Filtered chart data based on share configuration and auth status
   * @note If user is authenticated (JWT in headers), they may see unfiltered data if they are the author
   */
  async getSharedChart(shortCode: string, asGuest: boolean = false): Promise<SharedChartData> {
    try {
      // If asGuest is true, skip the Authorization header
      const config: any = {
        timeout: 10000
      };

      if (asGuest) {
        // Override headers to skip JWT
        config.headers = {
          'Content-Type': 'application/json'
          // No Authorization header
        };
        // Prevent interceptor from adding token
        config.skipAuthInterceptor = true;
      }

      const response = await api.get<SharedChartData>(
        `/charts/shared/${shortCode}`,
        config
      );

      return response.data;
    } catch (error) {
      if (this.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error('Invalid short code format.');
        }
        if (error.response?.status === 404) {
          throw new Error('Shared chart not found. The link may be invalid.');
        }
        if (error.response?.status === 410) {
          throw new Error('This shared link has expired.');
        }
      }
      this.handleError(error, 'Failed to load shared chart');
      throw error;
    }
  }

  /**
   * Get analytics for shared charts (admin only)
   *
   * @param shortCode - Optional specific short code
   * @param limit - Optional limit for results (default 100)
   * @returns Analytics data with access counts
   */
  async getShareAnalytics(shortCode?: string, limit?: number): Promise<ShareAnalytics[]> {
    try {
      const params = new URLSearchParams();
      if (shortCode) params.append('short_code', shortCode);
      if (limit) params.append('limit', limit.toString());

      const url = `/charts/analytics${params.toString() ? '?' + params.toString() : ''}`;

      const response = await api.get<ShareAnalytics[]>(url, {
        timeout: 10000
      });

      return response.data;
    } catch (error) {
      this.handleError(error, 'Failed to load share analytics');
      throw error;
    }
  }

  /**
   * Get list of share links for a specific chart
   *
   * Method 1: By short code (when managing existing shares)
   * Method 2: By chart parameters (when chart just loaded)
   *
   * @param params - Either shortCode OR chart parameters (datetime, latitude, longitude, timezone_offset, altitude)
   * @returns List of all share links for this specific chart
   */
  async getMyShares(params: {
    shortCode?: string;
    datetime?: string;
    latitude?: number;
    longitude?: number;
    timezoneOffset?: number;
    altitude?: number;
  }): Promise<MySharesResponse> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      if (params.shortCode) {
        // Method 1: By short code
        queryParams.append('short_code', params.shortCode);
      } else if (
        params.datetime &&
        params.latitude !== undefined &&
        params.longitude !== undefined &&
        params.timezoneOffset !== undefined
      ) {
        // Method 2: By chart parameters
        queryParams.append('datetime', params.datetime);
        queryParams.append('latitude', params.latitude.toString());
        queryParams.append('longitude', params.longitude.toString());
        queryParams.append('timezone_offset', params.timezoneOffset.toString());
        queryParams.append('altitude', (params.altitude || 0).toString());
      } else {
        throw new Error('Either shortCode or chart parameters (datetime, latitude, longitude, timezoneOffset) are required');
      }

      const response = await api.get<MySharesResponse>(
        `/charts/my-shares?${queryParams.toString()}`,
        { timeout: 10000 }
      );

      return response.data;
    } catch (error) {
      if (this.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error('Invalid parameters. Either short_code or chart parameters are required.');
        }
        if (error.response?.status === 404) {
          throw new Error('Chart not found.');
        }
        if (error.response?.status === 403) {
          throw new Error('Not authorized to view these shares.');
        }
      }
      this.handleError(error, 'Failed to load chart share links');
      throw error;
    }
  }

  /**
   * Delete a shared chart (admin or author only)
   *
   * @param shortCode - Short code to delete
   */
  async deleteSharedChart(shortCode: string): Promise<void> {
    try {
      await api.delete(`/charts/shared/${shortCode}`, {
        timeout: 10000
      });
    } catch (error) {
      if (this.isAxiosError(error)) {
        if (error.response?.status === 403) {
          throw new Error('Not authorized to delete this chart.');
        }
        if (error.response?.status === 404) {
          throw new Error('Shared chart not found.');
        }
      }
      this.handleError(error, 'Failed to delete shared chart');
      throw error;
    }
  }

  /**
   * Type guard for Axios errors
   */
  private isAxiosError(error: unknown): error is AxiosError {
    return (error as AxiosError).isAxiosError === true;
  }

  /**
   * Handle and log API errors
   */
  private handleError(error: unknown, message: string): void {
    if (this.isAxiosError(error)) {
      const status = error.response?.status;
      const details = error.response?.data;

      devError(`[ShortLinkApiService] ${message}`, {
        status,
        details,
        message: error.message
      });
    } else {
      devError(`[ShortLinkApiService] ${message}`, error);
    }
  }
}

// Export singleton instance
export const shortLinkApiService = new ShortLinkApiService();
